
  import { defineComponent, ref, onMounted, computed } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import * as Yup from 'yup';
  import { useI18n } from 'vue-i18n/index';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import LookupNameIds from '@/store/enums/settingsEnums';

  export default defineComponent({
    name: 'package-discount-creating',
    components: { ErrorMessage, Field, Form },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      const translate = (text) => (te(text) ? t(text) : text);
      const goBack = () => router.back();

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('create New Package Discount'), [
          translate('settings'),
        ]);
      });

      await store.dispatch(Actions.GET_ALL_SETTINGS_LOOKUPS, [
        LookupNameIds.CLASS_TYPE,
      ]);

      await store.dispatch(Actions.GET_ALL_CLASS_CATEGORIES);

      const validationSchema = Yup.object().shape({
        discountAmount: Yup.number().required(() =>
          translate('Discount Amount is required')
        ),

        numberOfLessons: Yup.number().required(() =>
          translate('Number of Lessons is required')
        ),

        packageTypeId: Yup.string().required(() =>
          translate('Package Type is required')
        ),

        classCategoryId: Yup.string().required(() =>
          translate('Class Category is required')
        ),
      });

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        await store.dispatch(Actions.CREATE_PACKAGE_DISCOUNT, values);

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: { confirmButton: 'btn fw-bold btn-light-danger' },
          });
        } else {
          Swal.fire({
            text: translate('Successfully created package discount'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: { confirmButton: 'btn btn-light-primary' },
          });
          router.push({ name: 'package-discount-listing' });
        }

        submitButton.value?.removeAttribute('data-kt-indicator');
        if (submitButton.value) submitButton.value.disabled = false;
      };

      const lookupsList = computed(() => store.getters.allLookupsList);

      const packageTypeList = lookupsList.value.filter((lookup) => {
        return lookup.lookupNameId === LookupNameIds.CLASS_TYPE;
      });

      const classCategoryList = computed(
        () => store.getters.allClassCategoriesList
      );

      return {
        translate,
        validationSchema,
        onSubmitCreate,
        goBack,
        submitButton,
        can,
        packageTypeList,
        classCategoryList,
      };
    },
  });
